import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import apiClient from "../../utils/apiClient";
import Shimmer from "../Common/Shimmer";
import ProtectedVideoPlayer from "../Common/ProtectedVideoPlayer";

/**
 * MaterialVideoModal — plays a course-level video material.
 *
 * Students never download a video (the server refuses them one); they stream
 * it from GET /materials/:id/stream through `ProtectedVideoPlayer` — our own
 * controls, no download / picture-in-picture / context menu, and the
 * student's name watermarked over the picture, full screen included.
 *
 *   <MaterialVideoModal material={playing} onClose={() => setPlaying(null)} />
 */
export default function MaterialVideoModal({ material, onClose }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!material?._id) return undefined;
    let alive = true;
    setUrl(null);
    setFailed(false);
    apiClient.get(`/materials/${material._id}/stream`)
      .then(({ data }) => {
        const next = data?.data?.url || null;
        if (!alive) return;
        if (next) setUrl(next);
        else setFailed(true);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [material?._id]);

  // The player's `onExpired`: a fresh link, swapped in without unmounting, so
  // playback carries on from where the old one stopped.
  const refresh = useCallback(() => {
    if (!material?._id) return;
    apiClient.get(`/materials/${material._id}/stream`)
      .then(({ data }) => { if (data?.data?.url) setUrl(data.data.url); })
      .catch(() => { /* the player offers "Try again" */ });
  }, [material?._id]);

  return (
    <Modal show={!!material} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-6 fw-semibold">{material?.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {failed ? (
          <p className="tw-m-0 tw-p-6 tw-text-center tw-text-sm tw-text-content-muted">This video could not be loaded.</p>
        ) : url ? (
          <ProtectedVideoPlayer
            src={url}
            title={material?.title}
            autoPlay
            onExpired={refresh}
            className="tw-aspect-video tw-max-h-[70vh] tw-w-full"
          />
        ) : (
          <div className="tw-p-4"><Shimmer height={320} /></div>
        )}
      </Modal.Body>
    </Modal>
  );
}
